import { getSessionId } from '@fs/zion-session'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from './useNotifications'

export default function useStreamDownload() {
  const { error } = useNotifications()
  const { t } = useTranslation()

  const streamDataDownload = useCallback(
    ({ requestPath, params, bodyFields }) => {
      if (document.getElementsByName('download_iframe').length > 0) {
        document.body.removeChild(document.getElementsByName('download_iframe')[0])
      }
      if (document.getElementsByName('download_form').length > 0) {
        document.body.removeChild(document.getElementsByName('download_form')[0])
      }

      // Adding an iframe prevents the page from redirecting to the download URL
      const iframe = document.createElement('iframe')
      iframe.name = 'download_iframe'
      iframe.setAttribute('data-testid', 'download_iframe')
      iframe.style.display = 'none'

      // Submitting a form allows the same streaming functionality that a link would allow,
      // but with the ability to pass the session ID in the body instead of the URL
      document.body.appendChild(iframe)
      const form = document.createElement('form')
      form.setAttribute('data-testid', 'download_form')
      form.name = 'download_form'
      form.target = iframe.name
      form.method = 'POST'
      form.action = `${requestPath}${params ? `?${new URLSearchParams(params).toString()}` : ''}`
      form.style.display = 'none'
      const input = document.createElement('input')
      input.name = 'sessionId'
      input.value = getSessionId()
      form.appendChild(input)

      if (bodyFields) {
        Object.entries(bodyFields).forEach(([key, value]) => {
          const addInput = document.createElement('input')
          addInput.name = key
          addInput.value = value
          form.appendChild(addInput)
        })
      }

      document.body.appendChild(form)

      // Error handling
      iframe.addEventListener('load', () => {
        try {
          const iframeDocument = iframe.contentWindow.document
          if (iframeDocument && iframeDocument.readyState === 'complete') {
            const isError = !iframeDocument.body.innerText.includes('"status":200')
            if (isError) {
              document.body.removeChild(form)
              error(t('downloadFileError', 'Error downloading file'))
            } else {
              document.body.removeChild(form)
            }
          }
        } catch {
          // Most likely a SecurityError due to cross-origin; treat as failure
          document.body.removeChild(form)
          error(t('downloadFileError', 'Error downloading file'))
        }
      })
      iframe.addEventListener('error', () => {
        document.body.removeChild(form)
        error(t('downloadFileError', 'Error downloading file'))
      })

      // Submit the form to initiate the download
      form.submit()
    },
    [error, t]
  )

  return { streamDataDownload }
}
